/* eslint no-param-reassign: "off" */

/**
 * Cloned from: https://github.com/acronis/merkle-patricia-tree.git
 *
 * Dependencies:
 * npm i --save keccak@3.0.1
 * npm i --save rlp@2.0.0
 *
 * Usage:
 * import MerkleTrie from '../store/MerklePatricialTree';
 * const Trie = new MerkleTrie();
 *
 * Trie.verifyProof(...);
 */
const TrieNode = require('./trieNode');
const rlp = require('rlp');
const createKeccakHash = require('keccak');

function matchingNibbleLength(nib1, nib2) {
  let i = 0;
  while (nib1[i] === nib2[i] && nib1.length > i) {
    i++;
  }
  return i;
}

function isHexPrefixed(str) {
  return str.slice(0, 2) === '0x';
}

function padToEven(a) {
  if (a.length % 2) a = `0${a}`;
  return a;
}

function stripHexPrefix(str) {
  if (typeof str !== 'string') {
    return str;
  }
  return isHexPrefixed(str) ? str.slice(2) : str;
}

function intToHex(i) {
  if (!(i % 1 === 0)) {
    console.log('number is not a integer');
  }
  if (!(i >= 0)) {
    console.log('number must be positive');
  }
  let hex = i.toString(16);
  if (hex.length % 2) {
    hex = `0${hex}`;
  }

  return `0x${hex}`;
}

function intToBuffer(i) {
  const hex = intToHex(i);
  return Buffer.from(hex.slice(2), 'hex');
}

function toBuffer(v) {
  if (!Buffer.isBuffer(v)) {
    if (Array.isArray(v)) {
      v = Buffer.from(v);
    } else if (typeof v === 'string') {
      if (isHexPrefixed(v)) {
        v = Buffer.from(padToEven(stripHexPrefix(v)), 'hex');
      } else {
        v = Buffer.from(v);
      }
    } else if (typeof v === 'number') {
      v = intToBuffer(v);
    } else if (v === null || v === undefined) {
      v = Buffer.from([]);
    } else if (v.toArray) {
      // converts a BN to a Buffer
      v = Buffer.from(v.toArray());
    } else {
      throw new Error('invalid type');
    }
  }
  return v;
}

function sha3(a) {
  a = toBuffer(a);

  const h = createKeccakHash('keccak256');
  if (a) {
    h.update(a);
  }

  return Buffer.from(h.digest('hex'), 'hex');
}

export default class MerkleTrie {
  verifyProof(rootHash, key, proof, cb) {
    key = TrieNode.stringToNibbles(key);
    let wantHash = toBuffer(rootHash);
    for (let i = 0; i < proof.length; i++) {
      const p = toBuffer(proof[i]);
      const hash = sha3(proof[i]);
      if (Buffer.compare(hash, wantHash)) {
        return cb(new Error(`bad proof node ${i}: hash mismatch`));
      }
      const node = new TrieNode(rlp.decode(p));
      let cld;
      if (node.type === 'branch') {
        if (key.length === 0) {
          if (i !== proof.length - 1) {
            return cb(new Error('additional nodes at end of proof'));
          }
          return cb(null, node.value);
        }
        cld = node.raw[key[0]];
        key = key.slice(1);
        if (cld.length === 2) {
          const embeddedNode = new TrieNode(cld);
          if (i !== proof.length - 1) {
            return cb(new Error('Key does not match with the proof one'));
          }

          if (matchingNibbleLength(embeddedNode.key, key) !== embeddedNode.key.length) {
            return cb(new Error('Key does not match with the proof one'));
          }
          key = key.slice(embeddedNode.key.length);
          if (key.length !== 0) {
            return cb(new Error('Key does not match with the proof one'));
          }
          return cb(null, embeddedNode.value);
        }
        wantHash = cld;
      } else if ((node.type === 'extention') || (node.type === 'leaf')) {
        if (matchingNibbleLength(node.key, key) !== node.key.length) {
          return cb(new Error('Key does not match with the proof one'));
        }
        cld = node.value;
        key = key.slice(node.key.length);
        if (key.length === 0) {
          if (i !== proof.length - 1) {
            return cb(new Error('Key does not match with the proof one'));
          }
          return cb(null, cld);
        }
        wantHash = cld;
      } else {
        return cb(new Error('Invalid node type'));
      }
    }
    cb(new Error('unexpected end of proof'));
    return this;
  }
}

